// src/services/filterConfigService.js
import { authService } from './authService';

export const filterConfigService = {
  // Fetch current filter configuration
  getConfig: async () => {
    console.log('[FilterConfigService] Fetching configuration');
    
    try {
      const response = await authService.authenticatedFetch('/message-filter/config');
      
      if (!response.ok) {
        const error = await response.json();
        console.error('[FilterConfigService] Fetch error:', error);
        throw new Error(error.message || 'Failed to fetch filter configuration');
      }

      const data = await response.json();
      console.log('[FilterConfigService] Configuration fetched:', data);
      
      return data;
    } catch (error) {
      console.error('[FilterConfigService] Error details:', error);
      throw error;
    }
  },

  // Update filter configuration
  updateConfig: async (config) => {
    console.log('[FilterConfigService] Updating configuration:', config);
    
    try {
      const response = await authService.authenticatedFetch('/message-filter/config', {
        method: 'PUT',
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('[FilterConfigService] Update error:', error);
        throw new Error(error.message || 'Failed to update filter configuration');
      }

      const data = await response.json();
      console.log('[FilterConfigService] Configuration updated:', data);
      
      return data;
    } catch (error) {
      console.error('[FilterConfigService] Error details:', error);
      throw error;
    }
  },

  // Add new keyword category
  addCategory: async (category) => {
    console.log('[FilterConfigService] Adding category:', category);
    
    try {
      const response = await authService.authenticatedFetch('/message-filter/categories', {
        method: 'POST',
        body: JSON.stringify(category)
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('[FilterConfigService] Add category error:', error);
        throw new Error(error.message || 'Failed to add category');
      }

      const data = await response.json();
      console.log('[FilterConfigService] Category added:', data);
      
      return data;
    } catch (error) {
      console.error('[FilterConfigService] Error details:', error);
      throw error;
    }
  },

  // Update keyword category
  updateCategory: async (categoryId, updates) => {
    console.log('[FilterConfigService] Updating category:', { categoryId, updates });
    
    try {
      const response = await authService.authenticatedFetch(
        `/message-filter/categories/${categoryId}`,
        {
          method: 'PUT',
          body: JSON.stringify(updates)
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error('[FilterConfigService] Update category error:', error);
        throw new Error(error.message || 'Failed to update category');
      }

      const data = await response.json();
      console.log('[FilterConfigService] Category updated:', data);
      
      return data;
    } catch (error) {
      console.error('[FilterConfigService] Error details:', error);
      throw error;
    }
  },

  // Delete keyword category
  deleteCategory: async (categoryId) => {
    console.log('[FilterConfigService] Deleting category:', categoryId);
    
    try {
      const response = await authService.authenticatedFetch(
        `/message-filter/categories/${categoryId}`,
        {
          method: 'DELETE'
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error('[FilterConfigService] Delete category error:', error);
        throw new Error(error.message || 'Failed to delete category');
      }

      console.log('[FilterConfigService] Category deleted:', categoryId);
    } catch (error) {
      console.error('[FilterConfigService] Error details:', error);
      throw error;
    }
  },

  // Add keywords to category
  addKeywords: async (categoryId, keywords) => {
    console.log('[FilterConfigService] Adding keywords:', { categoryId, keywords });
    
    try {
      const response = await authService.authenticatedFetch(
        `/message-filter/categories/${categoryId}/keywords`,
        {
          method: 'POST',
          body: JSON.stringify({ keywords })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error('[FilterConfigService] Add keywords error:', error);
        throw new Error(error.message || 'Failed to add keywords');
      }

      const data = await response.json();
      console.log('[FilterConfigService] Keywords added:', data);
      
      return data;
    } catch (error) {
      console.error('[FilterConfigService] Error details:', error);
      throw error;
    }
  },

  // Remove keywords from category
  removeKeywords: async (categoryId, keywords) => {
    console.log('[FilterConfigService] Removing keywords:', { categoryId, keywords });
    
    try {
      const response = await authService.authenticatedFetch(
        `/message-filter/categories/${categoryId}/keywords`,
        {
          method: 'DELETE',
          body: JSON.stringify({ keywords })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error('[FilterConfigService] Remove keywords error:', error);
        throw new Error(error.message || 'Failed to remove keywords');
      }

      const data = await response.json();
      console.log('[FilterConfigService] Keywords removed:', data);
      
      return data;
    } catch (error) {
      console.error('[FilterConfigService] Error details:', error);
      throw error;
    }
  },

  // Update severity thresholds
  updateThresholds: async (thresholds) => {
    console.log('[FilterConfigService] Updating thresholds:', thresholds);
    
    try {
      const response = await authService.authenticatedFetch(
        '/message-filter/thresholds',
        {
          method: 'PUT',
          body: JSON.stringify(thresholds)
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error('[FilterConfigService] Update thresholds error:', error);
        throw new Error(error.message || 'Failed to update thresholds');
      }

      const data = await response.json();
      console.log('[FilterConfigService] Thresholds updated:', data);
      
      return data;
    } catch (error) {
      console.error('[FilterConfigService] Error details:', error);
      throw error;
    }
  },

  // Get filter statistics
  getStats: async (timeframe = '24h') => {
    console.log('[FilterConfigService] Fetching stats:', { timeframe });
    
    try {
      const response = await authService.authenticatedFetch(
        `/message-filter/stats?timeframe=${timeframe}`
      );

      if (!response.ok) {
        const error = await response.json();
        console.error('[FilterConfigService] Get stats error:', error);
        throw new Error(error.message || 'Failed to fetch stats');
      }

      const data = await response.json();
      console.log('[FilterConfigService] Stats fetched:', data);
      
      return data;
    } catch (error) {
      console.error('[FilterConfigService] Error details:', error);
      throw error;
    }
  },

  // Test filter configuration against sample text
  testFilter: async (text) => {
    console.log('[FilterConfigService] Testing filter');
    
    try {
      const response = await authService.authenticatedFetch(
        '/message-filter/test',
        {
          method: 'POST',
          body: JSON.stringify({ text })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error('[FilterConfigService] Test filter error:', error);
        throw new Error(error.message || 'Failed to test filter');
      }

      const data = await response.json();
      console.log('[FilterConfigService] Filter test results:', data);
      
      return data;
    } catch (error) {
      console.error('[FilterConfigService] Error details:', error);
      throw error;
    }
  },

  // Import keyword list
  importKeywords: async (categoryId, file) => {
    console.log('[FilterConfigService] Importing keywords for category:', categoryId);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await authService.authenticatedFetch(
        `/message-filter/categories/${categoryId}/import`,
        {
          method: 'POST',
          headers: {
            // Don't set Content-Type, let browser set it with boundary
            'Accept': 'application/json'
          },
          body: formData
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error('[FilterConfigService] Import keywords error:', error);
        throw new Error(error.message || 'Failed to import keywords');
      }

      const data = await response.json();
      console.log('[FilterConfigService] Keywords imported:', data);
      
      return data;
    } catch (error) {
      console.error('[FilterConfigService] Error details:', error);
      throw error;
    }
  },

  // Export keyword list
  exportKeywords: async (categoryId) => {
    console.log('[FilterConfigService] Exporting keywords for category:', categoryId);
    
    try {
      const response = await authService.authenticatedFetch(
        `/message-filter/categories/${categoryId}/export`,
        {
          method: 'GET',
          headers: {
            'Accept': 'text/csv'
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error('[FilterConfigService] Export keywords error:', error);
        throw new Error(error.message || 'Failed to export keywords');
      }

      const blob = await response.blob();
      console.log('[FilterConfigService] Keywords exported');
      
      return blob;
    } catch (error) {
      console.error('[FilterConfigService] Error details:', error);
      throw error;
    }
  }
};

export default filterConfigService;